import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatEur, formatPercent } from "../../lib/format-kpi";
import type { InvestmentListItem } from "../../lib/investments";

export type SortOrder = "asc" | "desc";

/**
 * Vereinfachtes Comdirect-Style Depot:
 *
 *  [Avatar] Bezeichnung           | Stück | Kurs   | Wert    | Kauf    | G/V    | Div YTD | Anteil
 *           ISIN · TYPE · Symbol  |       | +diff% |         |         | %      |          | [bar] %
 *
 * Konvention: alle *Eur-Felder in CENTS, Pct-Quotienten kuerzen sich raus.
 */

export const DEFAULT_COLUMNS = [
  "name",
  "quantity",
  "price",
  "value",
  "purchaseValue",
  "gainLoss",
  "dividends",
  "depotShare",
] as const;

export const OPTIONAL_COLUMNS = [] as const;

export type ColumnKey = (typeof DEFAULT_COLUMNS)[number];

export const SUB_SORT_KEYS = [
  "priceDiffPct",
  "valueDiffAbs",
  "valueDiffPct",
] as const;

export type SubSortKey = (typeof SUB_SORT_KEYS)[number];

export type SortKey = ColumnKey | SubSortKey;

/**
 * Extrahiert die WKN aus ISIN (DE-ISIN: Stellen 7-12) oder faellt auf
 * Symbol zurueck. Maximal 12 Zeichen.
 */
export function extractWkn(isin: string | null, symbol: string): string {
  if (isin?.startsWith("DE") && isin.length === 12) return isin.slice(6, 12);
  return (isin ?? symbol).slice(0, 12);
}

function divRenditePct(p: InvestmentListItem): number | null {
  if (!p.periodReturn) return null;
  const divEur = p.periodReturn.dividendsNetEur;
  if (divEur === null) return null;
  const qty = Number(p.quantity);
  if (!Number.isFinite(qty) || qty <= 0) return null;
  if (p.purchasePriceCents <= 0) return null;
  const divPerShareCents = divEur / qty;
  return (divPerShareCents / p.purchasePriceCents) * 100;
}

const TYPE_BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  ETF: { bg: "rgba(59, 130, 246, 0.15)", fg: "#60a5fa" },
  AKTIE: { bg: "rgba(52, 211, 153, 0.15)", fg: "var(--pos)" },
  KRYPTO: { bg: "rgba(251, 191, 36, 0.15)", fg: "var(--warn)" },
  ANLEIHE: { bg: "rgba(148, 163, 184, 0.15)", fg: "var(--muted)" },
  ROHSTOFFE: { bg: "rgba(217, 119, 6, 0.15)", fg: "#d97706" },
  P2P: { bg: "rgba(236, 72, 153, 0.15)", fg: "#ec4899" },
  SONSTIGE: { bg: "rgba(148, 163, 184, 0.15)", fg: "var(--muted)" },
};

const AVATAR_PALETTE = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#a855f7",
  "#0ea5e9",
  "#ef4444",
  "#8b5cf6",
];
function colorForKey(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length] ?? "#3b82f6";
}

function formatNum(n: number, frac = 2): string {
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}

function formatQuantity(quantity: string, kind: string): string {
  if (kind === "P2P" || kind === "BETEILIGUNG") return "—";
  const n = Number(quantity);
  if (!Number.isFinite(n)) return quantity;
  const maxDigits = kind === "KRYPTO" ? 8 : kind === "SONSTIGE" ? 6 : 4;
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  });
}

function formatSignedEur(cents: number): string {
  const prefix = cents > 0 ? "+" : "";
  return `${prefix}${formatEur(cents)}`;
}
function formatSignedNum(n: number, frac = 2): string {
  const prefix = n > 0 ? "+" : "";
  return `${prefix}${formatNum(n, frac)}`;
}

const STRING_SORT_KEYS: ReadonlySet<SortKey> = new Set<SortKey>(["name"]);

function sortValueFor(key: SortKey, p: InvestmentListItem): string | number | null {
  switch (key) {
    case "name":
      return p.name;
    case "quantity":
      return Number(p.quantity);
    case "price":
      return p.latestQuote?.priceCents ?? null;
    case "value":
      return p.calc.marketValueEur ?? null;
    case "purchaseValue":
      return p.calc.purchaseValueEur ?? null;
    case "gainLoss":
      return p.calc.gainLossPercent ?? null;
    case "dividends":
      return p.periodReturn?.dividendsNetEur ?? 0;
    case "depotShare":
      return p.calc.marketValueEur ?? null;
    case "priceDiffPct":
      return p.calc.dailyChangePercent ?? null;
    case "valueDiffAbs": {
      const mv = p.calc.marketValueEur;
      const pv = p.calc.purchaseValueEur;
      return mv !== null && pv !== null ? mv - pv : null;
    }
    case "valueDiffPct":
      return p.calc.gainLossPercent ?? null;
  }
}

function sortItems(
  items: InvestmentListItem[],
  sort: SortKey,
  order: SortOrder,
): InvestmentListItem[] {
  const isString = STRING_SORT_KEYS.has(sort);
  const dir = order === "asc" ? 1 : -1;
  const copy = items.slice();
  copy.sort((a, b) => {
    const va = sortValueFor(sort, a);
    const vb = sortValueFor(sort, b);
    const aNull = va === null || (typeof va === "number" && !Number.isFinite(va));
    const bNull = vb === null || (typeof vb === "number" && !Number.isFinite(vb));
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (isString) {
      return String(va).localeCompare(String(vb), "de") * dir;
    }
    const na = Number(va);
    const nb = Number(vb);
    if (na < nb) return -1 * dir;
    if (na > nb) return 1 * dir;
    return 0;
  });
  return copy;
}

export function DepotTable({
  items,
  sort,
  order,
  onSortChange,
}: {
  items: InvestmentListItem[];
  visibleColumns?: readonly ColumnKey[];
  sort: SortKey;
  order: SortOrder;
  onSortChange: (nextSort: SortKey, nextOrder: SortOrder) => void;
}) {
  const { t } = useTranslation();
  const totalMv = items.reduce((s, i) => s + (i.calc.marketValueEur ?? 0), 0);
  const sortedItems = useMemo(() => sortItems(items, sort, order), [items, sort, order]);

  function handleSortClick(key: SortKey) {
    if (key === sort) onSortChange(key, order === "asc" ? "desc" : "asc");
    else onSortChange(key, "desc");
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr
            className="text-xs border-b"
            style={{ background: "var(--border-soft)", color: "var(--muted)", borderColor: "var(--border)" }}
          >
            <Th label={t("investments.table.column.name", { defaultValue: "Position" })} sortKey="name" sort={sort} order={order} onClick={handleSortClick} align="left" />
            <Th label={t("investments.table.column.quantity", { defaultValue: "Stueck" })} sortKey="quantity" sort={sort} order={order} onClick={handleSortClick} align="right" />
            <Th label={t("investments.table.column.price", { defaultValue: "Kurs" })} sortKey="price" sort={sort} order={order} onClick={handleSortClick} align="right" />
            <Th label={t("investments.table.column.value", { defaultValue: "Wert" })} sortKey="value" sort={sort} order={order} onClick={handleSortClick} align="right" />
            <Th label={t("investments.table.column.purchase_value", { defaultValue: "Kauf" })} sortKey="purchaseValue" sort={sort} order={order} onClick={handleSortClick} align="right" />
            <Th label={t("investments.table.column.gain_loss", { defaultValue: "G/V" })} sortKey="gainLoss" sort={sort} order={order} onClick={handleSortClick} align="right" />
            <Th label={t("investments.table.column.dividends_short", { defaultValue: "Div YTD" })} sortKey="dividends" sort={sort} order={order} onClick={handleSortClick} align="right" />
            <Th label={t("investments.table.column.depot_share", { defaultValue: "Depot-Anteil" })} sortKey="depotShare" sort={sort} order={order} onClick={handleSortClick} align="right" />
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((p) => (
            <Row key={p.id} p={p} totalMv={totalMv} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  label,
  sortKey,
  sort,
  order,
  onClick,
  align,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortKey;
  order: SortOrder;
  onClick: (k: SortKey) => void;
  align: "left" | "right";
}) {
  const active = sortKey === sort;
  const sym = active ? (order === "asc" ? "↑" : "↓") : "";
  const justify = align === "left" ? "justify-start" : "justify-end";
  return (
    <th
      className="px-3 py-2.5 font-semibold uppercase tracking-wider select-none"
      style={{
        textAlign: align,
        color: active ? "var(--accent)" : "var(--muted)",
      }}
      aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`flex items-center gap-1 ${justify} w-full bg-transparent border-0 cursor-pointer`}
        style={{ color: "inherit", fontWeight: "inherit" }}
      >
        <span className="whitespace-nowrap">{label}</span>
        {sym && (
          <span className="text-[10px]" aria-hidden="true">
            {sym}
          </span>
        )}
      </button>
    </th>
  );
}

function Row({ p, totalMv }: { p: InvestmentListItem; totalMv: number }) {
  const isP2P = p.kind === "P2P" || p.kind === "BETEILIGUNG";
  const cur = p.latestQuote !== null ? p.latestQuote.priceCents / 100 : null;
  const dayPct = p.calc.dailyChangePercent ?? null;
  const dayPctNeg = dayPct !== null && dayPct < 0;
  const mv = p.calc.marketValueEur;
  const pv = p.calc.purchaseValueEur;
  const gainEur = mv !== null && pv !== null ? mv - pv : null;
  const gainPct = p.calc.gainLossPercent;
  const gainNeg = gainEur !== null && gainEur < 0;

  const sharePct = mv !== null && totalMv > 0 ? (mv / totalMv) * 100 : null;
  const ytdDiv = p.periodReturn?.dividendsNetEur ?? 0;
  const yocPct = divRenditePct(p);

  const badge = TYPE_BADGE_COLORS[p.kind] ?? TYPE_BADGE_COLORS.SONSTIGE!;
  const avatarColor = colorForKey(p.id);
  const initial = p.name.trim().charAt(0).toUpperCase();
  const wkn = extractWkn(p.isin, p.symbol);

  return (
    <tr
      className="border-b transition-colors hover:bg-[var(--row-hover)]"
      style={{ borderColor: "var(--border-soft)" }}
    >
      {/* Position */}
      <td className="px-3 py-3 align-middle">
        <Link to={`/investments/${p.id}`} className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 rounded-md shrink-0 flex items-center justify-center text-white text-xs font-bold"
            style={{ background: avatarColor }}
            aria-hidden="true"
          >
            {initial}
          </div>
          <div className="min-w-0">
            <div
              className="font-medium text-sm truncate"
              style={{ color: "var(--text)" }}
            >
              {p.name}
            </div>
            <div className="text-[11px] mono mt-0.5 flex items-center gap-1.5 truncate" style={{ color: "var(--muted)" }}>
              <span>{wkn}</span>
              <Badge bg={badge.bg} fg={badge.fg}>{p.kind}</Badge>
              <span className="truncate">{p.symbol}</span>
            </div>
          </div>
        </Link>
      </td>

      {/* Stück */}
      <td className="px-3 py-3 align-middle text-right num text-sm">
        <span style={{ color: isP2P ? "var(--muted)" : "var(--text)" }}>
          {formatQuantity(p.quantity, p.kind)}
        </span>
      </td>

      {/* Kurs (mit Tages-Change) */}
      <td className="px-3 py-3 align-middle text-right">
        {isP2P ? (
          <span className="num text-sm" style={{ color: "var(--muted)" }}>—</span>
        ) : (
          <div className="num">
            <div className="text-sm">{cur !== null ? formatNum(cur, 2) : "—"}</div>
            {dayPct !== null && dayPct !== 0 && (
              <div
                className="text-[11px]"
                style={{ color: dayPctNeg ? "var(--neg)" : "var(--pos)" }}
              >
                {formatPercent(dayPct, 2)}
              </div>
            )}
          </div>
        )}
      </td>

      {/* Wert */}
      <td className="px-3 py-3 align-middle text-right num">
        <span className="text-sm font-semibold">
          {mv !== null ? formatEur(mv) : "—"}
        </span>
      </td>

      {/* Kauf */}
      <td className="px-3 py-3 align-middle text-right num text-sm" style={{ color: "var(--muted)" }}>
        {pv !== null ? formatEur(pv) : "—"}
      </td>

      {/* G/V */}
      <td className="px-3 py-3 align-middle text-right">
        {isP2P || gainEur === null ? (
          <span className="num text-sm" style={{ color: "var(--muted)" }}>—</span>
        ) : (
          <div className="num">
            <div
              className="text-sm font-semibold"
              style={{ color: gainNeg ? "var(--neg)" : "var(--pos)" }}
            >
              {formatSignedEur(gainEur)}
            </div>
            {gainPct !== null && (
              <div
                className="text-[11px]"
                style={{ color: gainNeg ? "var(--neg)" : "var(--pos)" }}
              >
                {formatPercent(gainPct, 2)}
              </div>
            )}
          </div>
        )}
      </td>

      {/* Div YTD */}
      <td className="px-3 py-3 align-middle text-right">
        {ytdDiv === 0 ? (
          <span className="num text-sm" style={{ color: "var(--muted)" }}>—</span>
        ) : (
          <div className="num">
            <div className="text-sm">{formatEur(ytdDiv)}</div>
            {yocPct !== null && yocPct > 0 && (
              <div className="text-[11px]" style={{ color: "var(--pos)" }}>
                {formatPercent(yocPct, 2)} YoC
              </div>
            )}
          </div>
        )}
      </td>

      {/* Depot-Anteil */}
      <td className="px-3 py-3 align-middle">
        {sharePct === null ? (
          <span className="num text-sm text-right block" style={{ color: "var(--muted)" }}>—</span>
        ) : (
          <div className="flex items-center gap-2 justify-end">
            <div
              className="h-1.5 rounded-full overflow-hidden w-16"
              style={{ background: "var(--border-soft)" }}
            >
              <div
                style={{
                  width: `${Math.min(100, sharePct).toFixed(1)}%`,
                  height: "100%",
                  background: "var(--accent)",
                }}
              />
            </div>
            <span className="num text-xs w-12 text-right">{formatNum(sharePct, 1)} %</span>
          </div>
        )}
      </td>
    </tr>
  );
}

function Badge({
  children,
  bg,
  fg,
}: {
  children: ReactNode;
  bg: string;
  fg: string;
}) {
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider"
      style={{ background: bg, color: fg }}
    >
      {children}
    </span>
  );
}

export { formatNum, formatSignedNum, sortItems };
